/**
 * Sprint 14.2 — Tech Assessment Bank.
 *
 * Mirror of the PSA (formerly MCQ) bank UI with two mandatory tagging fields:
 *   - Role (required at create + edit)
 *   - Department (required at create + edit)
 *
 * Together they scope each question to a specific role × department pair so
 * the runtime auto-pick-N flow (Phase 2 follow-up) can resolve a clean
 * question pool from the evaluation's pinned role + department.
 *
 * Intentional differences vs PSA bank:
 *   - No image upload (text-only at this stage)
 *   - No tag editor (role + dept ARE the taxonomy)
 *   - Filter bar surfaces Role + Department selects (with "All" defaults)
 */
import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch } from '@/lib/api';
import { useDerivedSelection } from '@/hooks/use-derived-state';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Save, Check, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { areOptionsUnique } from '@/lib/uniqueOptions';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';
import QuestionBankBulkUpload from '@/components/QuestionBankBulkUpload';
import QuestionBankReadOnlyBanner from '@/components/QuestionBankReadOnlyBanner';
import UnsavedRefreshGuard from '@/components/UnsavedRefreshGuard';
import RoleMultiSelect from '@/components/RoleMultiSelect';
import { Gated } from '@/auth/permissions';
import { bankWriteErrorMessage, useBankEditGuard } from '@/hooks/useBankEditGuard';
import {
  QuestionBankBulkDeleteBar,
  QuestionEditorDeleteButton,
  QuestionRowCheckbox,
  useCheckedQuestionIds,
} from '@/components/QuestionBankDeleteControls';

const EDIT_PERM = 'bank.tech.edit';
const API_PATH = '/questions/tech';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const STATUSES = ['draft', 'active', 'retired'];
const ALL = '__ALL__';

const BLANK_FORM = {
  text: '',
  options: ['', '', '', ''],
  correct: 0,
  difficulty: 'medium',
  topic: '',
  role_ids: [],
  department_id: '',
};

function isNewFormDirty(form) {
  return form.text.trim() !== ''
    || form.options.some((o) => o.trim() !== '')
    || form.correct !== BLANK_FORM.correct
    || form.difficulty !== BLANK_FORM.difficulty
    || form.topic !== ''
    || form.role_ids.length > 0
    || form.department_id !== '';
}

function getSavedRoleIds(q) {
  if (Array.isArray(q.role_ids) && q.role_ids.length) return q.role_ids;
  return q.role_id ? [q.role_id] : [];
}

function sameIdList(a, b) {
  const aa = [...a].sort();
  const bb = [...b].sort();
  return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
}

function isTechEditorDirty(q, { text, opts, correct, difficulty, topic, status, roleIds, deptId }) {
  return text !== q.text
    || correct !== q.correct_index
    || difficulty !== q.difficulty
    || (topic || '') !== (q.topic || '')
    || status !== q.status
    || deptId !== (q.department_id || '')
    || !sameIdList(roleIds, getSavedRoleIds(q))
    || opts.length !== q.options.length
    || opts.some((o, i) => o !== q.options[i]);
}

export default function TechAssessmentBank() {
  const qc = useQueryClient();
  const { readOnly, denyEdit, guard } = useBankEditGuard(EDIT_PERM);
  const [roleFilter, setRoleFilter] = useState(ALL);
  const [deptFilter, setDeptFilter] = useState(ALL);

  // Master-data sources for the filter + create-form selects.
  const { data: roles = [] } = useQuery({
    queryKey: ['v1-master-roles'],
    queryFn: () => v1Get('/master/roles'),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
  });

  // Question list — refetched when the filter changes.
  const { data: qs, isLoading, isError, error } = useQuery({
    queryKey: ['v1-tech-qs', roleFilter, deptFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (roleFilter !== ALL) params.set('role_id', roleFilter);
      if (deptFilter !== ALL) params.set('department_id', deptFilter);
      const qstr = params.toString();
      return v1Get(`/questions/tech${qstr ? `?${qstr}` : ''}`);
    },
  });

  const [selected, setSelected] = useDerivedSelection(qs);
  const [newOpen, setNewOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { checked, toggle, clear, setAll } = useCheckedQuestionIds();
  const [form, setForm] = useState({ ...BLANK_FORM });

  const { data: workCategories = [] } = useQuery({
    queryKey: ['v1-work-categories', form.department_id],
    queryFn: () => v1Get(`/questions/work-categories?department_id=${form.department_id}`),
    enabled: !!form.department_id,
  });

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="tech-assessment-bank-page">
        <PageHeader title="Tech Assessment Bank" />
        <Card className="card-elevated">
          <CardContent className="p-6 text-sm text-muted-foreground" data-testid="tech-loading">
            Loading tech questions…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6" data-testid="tech-assessment-bank-page">
        <PageHeader title="Tech Assessment Bank" />
        <Card className="card-elevated">
          <CardContent className="p-6 text-sm text-rose-600" data-testid="tech-error">
            Failed to load tech questions{error?.message ? `: ${error.message}` : ''}. Please retry, or contact an administrator if the problem persists.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!qs) return null;

  const resetForm = () => setForm({ ...BLANK_FORM });

  const handleNewDialogOpenChange = (open) => {
    if (!open) {
      if (isNewFormDirty(form)) {
        setNewOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetForm();
    }
    setNewOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isNewFormDirty(form)) {
        setNewOpen(true);
      }
    }
  };

  const confirmDiscardNewForm = () => {
    discardConfirmedRef.current = true;
    resetForm();
    setNewOpen(false);
  };

  const create = async () => {
    if (readOnly) return denyEdit();
    if (!form.text.trim()) return toast.error('Question text required');
    if (form.options.some((o) => !o.trim())) return toast.error('All four options must be filled');
    if (!areOptionsUnique(form.options)) {
      return toast.warning('Answer options must be unique. Please remove duplicate answers.');
    }
    if (!form.role_ids?.length) return toast.error('At least one role is required');
    if (!form.department_id) return toast.error('Department is required');
    if (!form.topic) return toast.error('Work category is required');
    try {
      const created = await v1Post('/questions/tech', {
        text: form.text,
        options: form.options,
        correct_index: form.correct,
        difficulty: form.difficulty,
        topic: form.topic,
        status: 'draft',
        role_ids: form.role_ids,
        department_id: form.department_id,
      });
      qc.invalidateQueries({ queryKey: ['v1-tech-qs'] });
      setSelected(created);
      setNewOpen(false);
      resetForm();
      toast.success('Tech question draft created');
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Failed to create question'));
    }
  };

  return (
    <div className="space-y-6" data-testid="tech-assessment-bank-page">
      <PageHeader
        title="Tech Assessment Bank"
        subtitle="Role × Department-scoped questions. Auto-picked at session time based on the evaluation's pinned role and department."
        actions={(
          <Gated need={EDIT_PERM}>
            <div className="flex gap-2">
              <Button variant="outline" className="gap-1.5" onClick={() => setBulkOpen(true)} data-testid="tech-bulk-upload-button">
                <FileUp className="h-4 w-4" /> Bulk upload
              </Button>
              <Button
                className="gap-1.5"
                onClick={() => setNewOpen(true)}
                data-testid="new-tech-q-button"
              >
                <Plus className="h-4 w-4" /> New question
              </Button>
            </div>
          </Gated>
        )}
      />

      {/* Filter bar — Role + Department */}
      <Card className="card-elevated">
        <CardContent className="flex flex-wrap items-center gap-3 p-3" data-testid="tech-filter-bar">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filter</p>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]" data-testid="tech-role-filter">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All roles</SelectItem>
              {roles
                .filter((r) => r.status === 'active')
                .map((r) => (
                  <SelectItem key={r.id} value={r.id}>{formatMasterOptionLabel(r)}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-[180px]" data-testid="tech-dept-filter">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All departments</SelectItem>
              {departments
                .filter((d) => d.status === 'active')
                .map((d) => (
                  <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          {(roleFilter !== ALL || deptFilter !== ALL) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setRoleFilter(ALL); setDeptFilter(ALL); }}
              data-testid="tech-filter-reset"
            >
              Reset
            </Button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {qs.length} {qs.length === 1 ? 'question' : 'questions'}
          </span>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="card-elevated">
          <CardContent className="flex max-h-[100vh] flex-col overflow-hidden p-3" data-testid="tech-question-list">
            <p className="shrink-0 px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Questions ({qs.length})
            </p>
            <div className="shrink-0">
              <QuestionBankBulkDeleteBar
                ids={(qs || []).map((q) => q.id)}
                checked={checked}
                toggle={toggle}
                setAll={setAll}
                clear={clear}
                apiPath={API_PATH}
                editPermission={EDIT_PERM}
                onDeleted={(ids) => {
                  const gone = new Set(Array.isArray(ids) ? ids : [ids]);
                  clear();
                  qc.invalidateQueries({ queryKey: ['v1-tech-qs'] });
                  if (selected && gone.has(selected.id)) setSelected(null);
                }}
                testIdPrefix="tech-bank"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {qs.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                  No tech questions yet — click <span className="font-medium">New question</span> to add the first one.
                </p>
              ) : (
                <ul className="space-y-1">
                  {qs.map((q) => (
                    <li key={q.id} className="flex items-start gap-2">
                      <QuestionRowCheckbox
                        id={q.id}
                        checked={checked.has(q.id)}
                        onToggle={toggle}
                        editPermission={EDIT_PERM}
                        testId={`tech-check-${q.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => setSelected(q)}
                        className={`flex min-w-0 flex-1 flex-col items-start gap-1 rounded-lg px-3 py-2 text-left text-sm transition-colors ${selected?.id === q.id ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                        data-testid={`tech-list-item-${q.id}`}
                      >
                        <span className="line-clamp-2">{q.text}</span>
                        <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground capitalize">
                          <span className="font-medium">
                            {(q.role_names && q.role_names.length)
                              ? q.role_names.join(', ')
                              : (q.role_name || '—')}
                          </span>
                          <span>·</span>
                          <span>{q.department_name || '—'}</span>
                          <span>·</span>
                          <span>{q.difficulty}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>

        {selected && (
          <TechEditor
            q={selected}
            roles={roles}
            departments={departments}
            key={selected.id}
            readOnly={readOnly}
            guard={guard}
            onChange={() => qc.invalidateQueries({ queryKey: ['v1-tech-qs'] })}
            onDelete={(deletedId) => {
              clear();
              qc.invalidateQueries({ queryKey: ['v1-tech-qs'] });
              if (selected?.id === deletedId) setSelected(null);
            }}
          />
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={newOpen} onOpenChange={handleNewDialogOpenChange}>
        <DialogContent className="sm:max-w-2xl" data-testid="new-tech-q-dialog">
          <DialogHeader>
            <DialogTitle>New tech assessment question</DialogTitle>
            <DialogDescription>
              Roles + Department are mandatory — they determine which evaluations this question is offered to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Question text</label>
              <Textarea
                rows={3}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="In React 18, which hook prevents stale closures in event handlers?"
                data-testid="tech-new-text"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Options (click circle to mark correct)</label>
              {form.options.map((o, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 rounded-lg border p-2 ${i === form.correct ? 'border-emerald-300 bg-emerald-50' : 'bg-card'}`}
                >
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, correct: i })}
                    className={`grid h-6 w-6 place-items-center rounded-full border text-[11px] font-semibold ${i === form.correct ? 'border-emerald-500 bg-emerald-500 text-white' : 'text-muted-foreground'}`}
                  >
                    {i === form.correct ? <Check className="h-3.5 w-3.5" /> : String.fromCharCode(65 + i)}
                  </button>
                  <Input
                    value={o}
                    onChange={(e) => {
                      const n = [...form.options];
                      n[i] = e.target.value;
                      setForm({ ...form, options: n });
                    }}
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    data-testid={`tech-new-option-${i}`}
                  />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  Roles <span className="text-rose-600">*</span>
                </label>
                <RoleMultiSelect
                  roles={roles}
                  value={form.role_ids}
                  onChange={(role_ids) => setForm({ ...form, role_ids })}
                  testId="tech-new-roles"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  Department <span className="text-rose-600">*</span>
                </label>
                <Select
                  value={form.department_id}
                  onValueChange={(v) => setForm({ ...form, department_id: v, topic: '' })}
                >
                  <SelectTrigger data-testid="tech-new-dept">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.filter((d) => d.status === 'active').map((d) => (
                      <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Difficulty</label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger className="capitalize"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map((d) => (
                      <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">
                  Category <span className="text-rose-600">*</span>
                </label>
                <Select
                  value={form.topic}
                  onValueChange={(v) => setForm({ ...form, topic: v })}
                  disabled={!form.department_id}
                >
                  <SelectTrigger data-testid="tech-new-category">
                    <SelectValue placeholder={form.department_id ? 'Select category' : 'Pick department first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {workCategories.map((c) => (
                      <SelectItem key={c.id || c.label} value={c.label}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.department_id && workCategories.length === 0 && (
                  <p className="mt-1 text-[11px] text-amber-800">
                    No categories for this department.{' '}
                    <Link to="/portal/settings/roles" className="font-medium underline underline-offset-2">
                      Add categories in Roles &amp; Departments
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => handleNewDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={create} data-testid="new-tech-q-confirm">Create question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="new-tech-q-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered question data will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="new-tech-q-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardNewForm} data-testid="new-tech-q-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedRefreshGuard
        armed={newOpen && isNewFormDirty(form)}
        testIdPrefix="new-tech-q"
      />

      <QuestionBankBulkUpload
        kind="tech"
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        roles={roles}
        departments={departments}
        onImported={() => qc.invalidateQueries({ queryKey: ['v1-tech-qs'] })}
      />
    </div>
  );
}

function TechEditor({ q, roles, departments, readOnly, guard, onChange, onDelete }) {
  const [text, setText] = useState(q.text);
  const [opts, setOpts] = useState(q.options);
  const [correct, setCorrect] = useState(q.correct_index);
  const [difficulty, setDifficulty] = useState(q.difficulty);
  const [topic, setTopic] = useState(q.topic || '');
  const [status, setStatus] = useState(q.status);
  const initialRoles = Array.isArray(q.role_ids) && q.role_ids.length
    ? q.role_ids
    : (q.role_id ? [q.role_id] : []);
  const [roleIds, setRoleIds] = useState(initialRoles);
  const [deptId, setDeptId] = useState(q.department_id || '');
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(
    () => isTechEditorDirty(q, { text, opts, correct, difficulty, topic, status, roleIds, deptId }),
    [q, text, opts, correct, difficulty, topic, status, roleIds, deptId],
  );

  const { data: workCategories = [] } = useQuery({
    queryKey: ['v1-work-categories', deptId],
    queryFn: () => v1Get(`/questions/work-categories?department_id=${deptId}`),
    enabled: !!deptId,
  });

  const save = guard(async () => {
    if (saving || !dirty) return;
    if (opts.some((o) => !o.trim())) return toast.error('All four options must be filled');
    if (!areOptionsUnique(opts)) {
      return toast.warning('Answer options must be unique. Please remove duplicate answers.');
    }
    if (!roleIds?.length) return toast.error('At least one role is required');
    if (!deptId) return toast.error('Department is required');
    if (!topic) return toast.error('Work category is required');
    setSaving(true);
    try {
      await v1Patch(`/questions/tech/${q.id}`, {
        text,
        options: opts,
        correct_index: correct,
        difficulty,
        topic,
        status,
        role_ids: roleIds,
        department_id: deptId,
      });
      toast.success('Question saved');
      onChange?.();
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="card-elevated">
      <CardContent className="space-y-5 p-6" data-testid="tech-editor-form">
        {readOnly && <QuestionBankReadOnlyBanner testId="tech-editor-read-only" />}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Editing Tech question</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={setStatus} disabled={readOnly}>
              <SelectTrigger className="w-32" data-testid="tech-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Question text</label>
          <Textarea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={readOnly}
            data-testid="tech-editor-text"
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Options (mark correct)</label>
          {opts.map((o, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-lg border p-2 ${i === correct ? 'border-emerald-300 bg-emerald-50' : 'bg-card'}`}
            >
              {/* Left clickable while read-only so the refusal can be reported;
                  a disabled button would swallow the click silently. */}
              <button
                type="button"
                onClick={guard(() => setCorrect(i))}
                className={`grid h-6 w-6 place-items-center rounded-full border text-[11px] font-semibold ${i === correct ? 'border-emerald-500 bg-emerald-500 text-white' : 'text-muted-foreground'} ${readOnly ? 'cursor-not-allowed' : ''}`}
                data-testid={`tech-editor-correct-${i}`}
              >
                {i === correct ? <Check className="h-3.5 w-3.5" /> : String.fromCharCode(65 + i)}
              </button>
              <Input
                value={o}
                onChange={(e) => {
                  const n = [...opts];
                  n[i] = e.target.value;
                  setOpts(n);
                }}
                disabled={readOnly}
                data-testid={`tech-editor-option-${i}`}
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Roles <span className="text-rose-600">*</span>
            </label>
            <RoleMultiSelect
              roles={roles}
              value={roleIds}
              onChange={setRoleIds}
              disabled={readOnly}
              testId="tech-editor-roles"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Department <span className="text-rose-600">*</span>
            </label>
            <Select value={deptId} onValueChange={(v) => { setDeptId(v); setTopic(''); }} disabled={readOnly}>
              <SelectTrigger data-testid="tech-editor-dept"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>
                {departments.filter((d) => d.status === 'active').map((d) => (
                  <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Difficulty</label>
            <Select value={difficulty} onValueChange={setDifficulty} disabled={readOnly}>
              <SelectTrigger className="capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DIFFICULTIES.map((d) => (
                  <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Category <span className="text-rose-600">*</span>
            </label>
            <Select value={topic} onValueChange={setTopic} disabled={readOnly || !deptId}>
              <SelectTrigger data-testid="tech-editor-category">
                <SelectValue placeholder={deptId ? 'Select category' : 'Pick department first'} />
              </SelectTrigger>
              <SelectContent>
                {workCategories.map((c) => (
                  <SelectItem key={c.id || c.label} value={c.label}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {deptId && workCategories.length === 0 && (
              <p className="mt-1 text-[11px] text-amber-800">
                No categories for this department.{' '}
                <Link to="/portal/settings/roles" className="font-medium underline underline-offset-2">
                  Add categories in Roles &amp; Departments
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <QuestionEditorDeleteButton
            apiPath={API_PATH}
            questionId={q.id}
            questionPreview={text}
            editPermission={EDIT_PERM}
            onDeleted={onDelete}
            testId="tech-editor-delete"
          />
          <Button onClick={save} disabled={saving || readOnly || !dirty} className="gap-1.5" data-testid="tech-editor-save">
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save question'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
